import { useState, useEffect, useCallback, useRef } from 'react';
import request from '../../utils/request';
import { useAuthStore, hasFunction } from '../../stores/authStore';
import CreateEmployeeModal from './CreateEmployeeModal';
import EditEmployeeModal from './EditEmployeeModal';
import ResetPasswordModal from './ResetPasswordModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import AlertModal from '../../components/AlertModal/AlertModal';
import { MobileResponsiveTable } from '../../components/MobileResponsiveTable';

interface EmployeeItem {
  id: number;
  name: string;
  email: string;
  role_id: number;
  role_name: string;
  status: number;
  created_at: string;
  updated_at: string;
}

export default function EmployeeListPage() {
  const fetchedRef = useRef(false);
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<EmployeeItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState<EmployeeItem | null>(null);
  const [isResetPwdModalOpen, setIsResetPwdModalOpen] = useState(false);
  const [resetPwdEmployee, setResetPwdEmployee] = useState<EmployeeItem | null>(null);

  // 权限检查
  const canCreate = hasFunction(user, 'employee_create');
  const canEdit = hasFunction(user, 'employee_edit');
  const canDelete = hasFunction(user, 'employee_delete');
  const canToggleStatus = hasFunction(user, 'employee_toggle_status');
  const canResetPassword = hasFunction(user, 'employee_reset_password');

  // Confirm modal state
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'warning' | 'danger' | 'info';
    loading: boolean;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'warning',
    loading: false,
    onConfirm: () => {},
  });

  // Alert modal state
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    title: string;
    content: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    isOpen: false,
    title: '',
    content: '',
    type: 'error',
  });

  // Fetch employee list
  const fetchEmployees = useCallback(async () => {
    setLoading(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const response: any = await request.get('/gradmin/employees');
      if (response.code === 0) {
        setEmployees(response.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch employee list:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    // Prevent duplicate requests in React StrictMode
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    fetchEmployees();
  }, [fetchEmployees]);

  const handleCreateSuccess = () => {
    fetchEmployees();
  };

  const showConfirm = (
    title: string,
    content: string,
    type: 'warning' | 'danger' | 'info',
    onConfirm: () => void
  ) => {
    setConfirmModal({
      isOpen: true,
      title,
      content,
      type,
      loading: false,
      onConfirm,
    });
  };

  const closeConfirm = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
  };

  const setConfirmLoading = (loading: boolean) => {
    setConfirmModal(prev => ({ ...prev, loading }));
  };

  const showAlert = (
    title: string,
    content: string,
    type: 'success' | 'error' | 'warning' | 'info' = 'error'
  ) => {
    setAlertModal({
      isOpen: true,
      title,
      content,
      type,
    });
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  // Activate employee
  const handleEnable = (employee: EmployeeItem) => {
    showConfirm(
      'Activate Employee',
      `Are you sure you want to activate employee "${employee.name}"?`,
      'warning',
      async () => {
        try {
          setConfirmLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any = await request.patch(`/gradmin/employees/${employee.id}/status`, {
            status: 1,
          });
          if (response.code === 0) {
            fetchEmployees();
          }
          closeConfirm();
        } catch (err) {
          closeConfirm();
          showAlert('Action Failed', err instanceof Error ? err.message : 'Action failed', 'error');
        }
      }
    );
  };

  // Disable employee
  const handleDisable = (employee: EmployeeItem) => {
    showConfirm(
      'Disable Employee',
      `Are you sure you want to disable employee "${employee.name}"?`,
      'warning',
      async () => {
        try {
          setConfirmLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any = await request.patch(`/gradmin/employees/${employee.id}/status`, {
            status: 2,
          });
          if (response.code === 0) {
            fetchEmployees();
          }
          closeConfirm();
        } catch (err) {
          closeConfirm();
          showAlert('Action Failed', err instanceof Error ? err.message : 'Action failed', 'error');
        }
      }
    );
  };

  // Open reset password modal
  const handleResetPassword = (employee: EmployeeItem) => {
    setResetPwdEmployee(employee);
    setIsResetPwdModalOpen(true);
  };

  // Close reset password modal
  const handleCloseResetPwdModal = () => {
    setIsResetPwdModalOpen(false);
    setResetPwdEmployee(null);
  };

  // Reset password success callback
  const handleResetPasswordSuccess = () => {
    showAlert('Reset Successful', 'Password has been reset', 'success');
  };

  // Delete employee
  const handleDelete = (employee: EmployeeItem) => {
    showConfirm(
      'Delete Employee',
      `Are you sure you want to delete employee "${employee.name}"? This action cannot be undone.`,
      'danger',
      async () => {
        try {
          setConfirmLoading(true);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const response: any = await request.delete(`/gradmin/employees/${employee.id}`);
          if (response.code === 0) {
            fetchEmployees();
          }
          closeConfirm();
        } catch (err) {
          closeConfirm();
          showAlert('Delete Failed', err instanceof Error ? err.message : 'Delete failed', 'error');
        }
      }
    );
  };

  // Open edit modal
  const handleEdit = (employee: EmployeeItem) => {
    setEditingEmployee(employee);
    setIsEditModalOpen(true);
  };

  // Close edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingEmployee(null);
  };

  // Edit success callback
  const handleEditSuccess = () => {
    fetchEmployees();
    setEditingEmployee(null);
  };

  const getStatusText = (status: number) => {
    return status === 1 ? 'Active' : 'Disabled';
  };

  const getStatusClass = (status: number) => {
    return status === 1 ? 'page-status-active' : 'page-status-inactive';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('zh-CN');
  };

  return (
    <div className="min-h-full">
      <div className="page-header">
        <h1 className="page-title">Employee List</h1>
      </div>

      {/* Action bar */}
      <div className="page-action-bar">
        {canCreate && (
          <button className="page-add-button" onClick={() => setIsModalOpen(true)}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
            Add Employee
          </button>
        )}
      </div>

      {/* Data table */}
      <MobileResponsiveTable
        containerClassName="page-table-container"
        tableClassName="page-data-table"
      >
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Created At</th>
            <th>Updated At</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={7} className="page-loading-cell">
                <div className="page-loading-spinner"></div>
                Loading...
              </td>
            </tr>
          ) : employees.length === 0 ? (
            <tr>
              <td colSpan={7} className="page-empty-cell">
                No data
              </td>
            </tr>
          ) : (
            employees.map(employee => (
              <tr key={employee.id}>
                <td>{employee.name}</td>
                <td>{employee.email}</td>
                <td>{employee.role_name}</td>
                <td>
                  <span className={`page-status-badge ${getStatusClass(employee.status)}`}>
                    {getStatusText(employee.status)}
                  </span>
                </td>
                <td>{formatDate(employee.created_at)}</td>
                <td>{formatDate(employee.updated_at)}</td>
                <td>
                  <div className="page-action-buttons">
                    {canToggleStatus && employee.status === 2 && (
                      <button
                        className="page-action-btn page-action-enable"
                        onClick={() => handleEnable(employee)}
                        title="Activate"
                      >
                        Activate
                      </button>
                    )}
                    {canToggleStatus && employee.status === 1 && (
                      <button
                        className="page-action-btn page-action-disable"
                        onClick={() => handleDisable(employee)}
                        title="Disable"
                      >
                        Disable
                      </button>
                    )}
                    {canResetPassword && (
                      <button
                        className="page-action-btn page-action-reset"
                        onClick={() => handleResetPassword(employee)}
                        title="Reset Password"
                      >
                        Reset Password
                      </button>
                    )}
                    {canEdit && (
                      <button
                        className="page-action-btn page-action-edit"
                        onClick={() => handleEdit(employee)}
                        title="Edit"
                      >
                        Edit
                      </button>
                    )}
                    {canDelete && (
                      <button
                        className="page-action-btn page-action-delete"
                        onClick={() => handleDelete(employee)}
                        title="Delete"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </MobileResponsiveTable>

      <CreateEmployeeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <EditEmployeeModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSuccess={handleEditSuccess}
        employeeData={editingEmployee}
      />

      <ResetPasswordModal
        isOpen={isResetPwdModalOpen}
        onClose={handleCloseResetPwdModal}
        onSuccess={handleResetPasswordSuccess}
        employeeId={resetPwdEmployee?.id || null}
        employeeName={resetPwdEmployee?.name || ''}
      />

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        content={confirmModal.content}
        type={confirmModal.type}
        loading={confirmModal.loading}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        title={alertModal.title}
        content={alertModal.content}
        type={alertModal.type}
        confirmText="OK"
        onConfirm={closeAlert}
      />
    </div>
  );
}
